// i18n HOSER - Add new countries/locales/TLDs to `TOP_LEVEL_DOMAINS`,
// `STAGING_PREFIXES`, `LOCALES`, `StagingPrefixByTopLevelDomain`, and
// `LocalesByTopLevelDomain` below before rolling them out.

// The source of truth for the countries and locales TaskRabbit supports can be
// found in the supported_locale_map in `config/settings.yml`.

/**
 * These are the top-level domains of all the websites that TaskRabbit
 * operates. (www.taskrabbit.com, www.taskrabbit.ca, etc.)
 */
export const TOP_LEVEL_DOMAINS = [
  "ca",
  "co.uk",
  "com",
  "de",
  "es",
  "fr",
  "it",
  "pt",
] as const;
export type TopLevelDomain = typeof TOP_LEVEL_DOMAINS[number];
export function isTopLevelDomain(value: any): value is TopLevelDomain {
  return TOP_LEVEL_DOMAINS.includes(value);
}

/**
 * These are the staging prefixes for all the websites that TaskRabbit
 * operates. (us-staging-1.taskrabbit.in, ca-staging-1.taskrabbit.in, etc.)
 */
const STAGING_PREFIXES = [
  "ca",
  "de",
  "es",
  "fr",
  "uk",
  "us",
  "pt",
  "it",
] as const;
export type StagingPrefix = typeof STAGING_PREFIXES[number];

/**
 * These are all of the locales that TaskRabbit serves.
 */
const LOCALES = [
  "ca-ES",
  "de-DE",
  "en-CA",
  "en-GB",
  "en-US",
  "es-ES",
  "fr-CA",
  "fr-FR",
  "it-IT",
  "pt-PT",
] as const;
export type Locale = typeof LOCALES[number];
export function isLocale(value: any): value is Locale {
  return LOCALES.includes(value);
}

export const StagingPrefixByTopLevelDomain: Record<
  TopLevelDomain,
  StagingPrefix
> = {
  ca: "ca",
  com: "us",
  "co.uk": "uk",
  es: "es",
  de: "de",
  fr: "fr",
  it: "it",
  pt: "pt",
};

const LocalesByTopLevelDomain: Record<TopLevelDomain, Locale[]> = {
  ca: ["en-CA", "fr-CA"],
  com: ["en-US"],
  "co.uk": ["en-GB"],
  de: ["de-DE"],
  es: ["ca-ES", "es-ES"],
  fr: ["fr-FR"],
  it: ["it-IT"],
  pt: ["pt-PT"],
};

/**
 * Returns the top-level domain for the site that is currently being built
 * (e.g. "com", "co.uk", "ca", etc.)
 */
export function getTLD() {
  const gatsbyTLD = process.env.GATSBY_TLD;
  if (isTopLevelDomain(gatsbyTLD)) {
    return gatsbyTLD;
  }
  throw Error(
    `The environment variable GATSBY_TLD must be set to a valid top-level ` +
      `domain. It should be set to one of the TLDs at which TaskRabbit ` +
      `operates a website (e.g. ${TOP_LEVEL_DOMAINS}).`
  );
}

/**
 * Returns the array of supported locales for the current top-level domain.
 */
export function getLocales() {
  return LocalesByTopLevelDomain[getTLD()];
}

function isMultiLanguageSupported() {
  return getLocales().length > 1;
}

export function multiLanguagePrefix(locale: Locale) {
  if (isMultiLanguageSupported()) {
    const countryCode = countryCodeFromLocale(locale);
    const languageCode = languageCodeFromLocale(locale);
    return `/${countryCode}/${languageCode}`;
  }
  throw Error(`There is no multi-language prefix for the locale "${locale}"`);
}

function countryCodeFromLocale(locale: Locale) {
  return locale.slice(-2).toLowerCase();
}

export function languageCodeFromLocale(locale: Locale) {
  return locale.slice(0, 2).toLowerCase();
}
